import { apiRequest } from "./client";

export type ProjectStatus = "SUBMITTED" | "COMPLETED";
export type ProjectReviewAssignmentStatus = "ASSIGNED" | "UNASSIGNED";

export interface MetadataItem {
  code: string;
  label: string;
  sort_order: number;
}

export interface ProjectMetadata {
  work_types: MetadataItem[];
  rights_modes: MetadataItem[];
}

export interface ProjectListItem {
  id: string;
  project_number: string;
  owner_user_id: string;
  owner_name: string;
  work_name: string;
  work_type_code: string;
  rights_mode_code: string;
  completion_date: string;
  publication_date: string | null;
  status: ProjectStatus;
  version: number;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectParty {
  registrant_id: string;
  public_id: string;
  current_name: string;
  name_snapshot: string;
  is_registrant_deleted: boolean;
  sort_order: number;
}

export interface ProjectDetail extends ProjectListItem {
  agent_name: string | null;
  contact_phone: string | null;
  cloud_drive_info: string | null;
  work_summary: string | null;
  creation_process: string | null;
  authors: ProjectParty[];
  right_holders: ProjectParty[];
}

export interface ProjectListResponse {
  items: ProjectListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ProjectBatchActionResponse {
  processed_count: number;
  revoked_assignment_count: number;
}

export interface ProjectCloudDriveInfoResponse {
  cloud_drive_info: string | null;
}

export interface ProjectWritePayload {
  work_name: string;
  work_type_code: string;
  rights_mode_code: string;
  completion_date: string;
  publication_date: string | null;
  author_ids: string[];
  right_holder_ids: string[];
  agent_name: string | null;
  contact_phone: string | null;
  cloud_drive_info: string | null;
  work_summary: string;
  creation_process: string;
}

export function fetchProjectMetadata(signal?: AbortSignal): Promise<ProjectMetadata> {
  return apiRequest("/api/v1/metadata/projects", { signal });
}

export function fetchProjects(
  filters: {
    page: number;
    pageSize: number;
    search?: string;
    status?: ProjectStatus;
    workTypeCode?: string;
    ownerUserId?: string;
    reviewAssignmentStatus?: ProjectReviewAssignmentStatus;
    submittedDate?: string;
    deleted?: boolean;
  },
  signal?: AbortSignal,
): Promise<ProjectListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
  });
  if (filters.search) params.set("search", filters.search);
  if (filters.status) params.set("status", filters.status);
  if (filters.workTypeCode) params.set("work_type_code", filters.workTypeCode);
  if (filters.ownerUserId) params.set("owner_user_id", filters.ownerUserId);
  if (filters.reviewAssignmentStatus) {
    params.set("review_assignment_status", filters.reviewAssignmentStatus);
  }
  if (filters.submittedDate) params.set("submitted_date", filters.submittedDate);
  if (filters.deleted) params.set("deleted", "true");
  return apiRequest(`/api/v1/projects?${params}`, { signal });
}

export function fetchProjectDetail(id: string): Promise<ProjectDetail> {
  return apiRequest(`/api/v1/projects/${id}`);
}

export function fetchProjectCloudDriveInfo(id: string): Promise<ProjectCloudDriveInfoResponse> {
  return apiRequest(`/api/v1/projects/${id}/cloud-drive-info`);
}

export function recordProjectCloudDriveInfoCopy(id: string): Promise<void> {
  return apiRequest(`/api/v1/projects/${id}/cloud-drive-info/copy-audit`, { method: "POST" });
}

export function createProject(payload: ProjectWritePayload): Promise<ProjectDetail> {
  return apiRequest("/api/v1/projects", { method: "POST", body: JSON.stringify(payload) });
}

export function updateProject(
  id: string,
  payload: ProjectWritePayload & { version: number },
): Promise<ProjectDetail> {
  return apiRequest(`/api/v1/projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function updateProjectStatus(
  id: string,
  status: ProjectStatus,
  version: number,
): Promise<ProjectDetail> {
  return apiRequest(`/api/v1/projects/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, version }),
  });
}

export function batchUpdateProjectStatus(
  projects: Array<{ id: string; version: number }>,
  status: ProjectStatus,
): Promise<ProjectBatchActionResponse> {
  return apiRequest("/api/v1/projects/batch/status", {
    method: "POST",
    body: JSON.stringify({ projects, status }),
  });
}

export function batchDeleteProjects(projectIds: string[]): Promise<ProjectBatchActionResponse> {
  return apiRequest("/api/v1/projects/batch/delete", {
    method: "POST",
    body: JSON.stringify({ project_ids: projectIds }),
  });
}

export function deleteProject(id: string): Promise<void> {
  return apiRequest(`/api/v1/projects/${id}`, { method: "DELETE" });
}

export function restoreProject(id: string): Promise<ProjectDetail> {
  return apiRequest(`/api/v1/projects/${id}/restore`, { method: "POST" });
}
